import { roundTo2Decimals } from '@principle-theorem/accounting';
import {
  CollectionGroup,
  IAccountCredit,
  IPatient,
  IPractice,
  ITransaction,
  IUsedAccountCredit,
  TransactionProvider,
  TransactionStatus,
  TransactionType,
  isAccountCreditExtendedData,
} from '@principle-theorem/principle-core/interfaces';
import {
  IInvoiceReportRequest,
  ITransactionReportRequest,
} from '@principle-theorem/reporting';
import {
  DocumentReference,
  Firestore,
  IDataTable,
  ISODateType,
  ITimePeriod,
  WithRef,
  asReffable,
  asyncForAll,
  chunkRange,
  collectionGroupQuery,
  isSameRef,
  multiFilter,
  multiMap,
  multiMergeMap,
  query,
  reduce2DArray,
  toISODate,
  toTimePeriod,
  toTimestamp,
  where,
} from '@principle-theorem/shared';
import { differenceWith, groupBy, sortBy, sum, uniqWith } from 'lodash';
import { from, of } from 'rxjs';
import { defaultIfEmpty, mergeMap, scan } from 'rxjs/operators';
import { Brand } from '../models/brand';
import { Practice } from '../models/practice/practice';
import { dateRangeToPracticeTimezone } from './helpers';

interface IUsedAccountCreditWithType extends IUsedAccountCredit {
  type: TransactionType;
  provider: TransactionProvider;
}

interface IAccountCreditFromOtherPractice {
  creditCreatedAt: ISODateType;
  creditPracticeName: string;
  creditPatientName: string;
  creditId: string;
  creditPracticeAmount: number;
  creditPracticeRemainingAtDate: number;
  creditPracticeUsedAtDate: number;
}

const headers: Record<keyof IAccountCreditFromOtherPractice, string> = {
  creditCreatedAt: 'Created At',
  creditPracticeName: 'Created at Practice',
  creditPatientName: 'Patient Name',
  creditId: 'Credit ID',
  creditPracticeAmount: 'Credit Amount Billed by Practice',
  creditPracticeUsedAtDate: 'Credit Allocated by Practice At Date',
  creditPracticeRemainingAtDate: 'Credit Remaining by Practice At Date',
};

export class AccountCreditsUsedUpUntilDate {
  async run(
    dateRange: ITimePeriod,
    practiceRef: DocumentReference<IPractice>
  ): Promise<IDataTable<IAccountCreditFromOtherPractice>> {
    const brandRef = Practice.brandRef(asReffable(practiceRef));
    const practice = await Firestore.getDoc(practiceRef);
    const brand = await Firestore.getDoc(brandRef);
    const practices = await Firestore.getDocs(Brand.practiceCol(brand));
    const rows: IAccountCreditFromOtherPractice[] = [];

    try {
      const practiceDateRange = dateRangeToPracticeTimezone(
        practice,
        dateRange
      );
      const practiceAccountCreditTransactions =
        await getAccountCreditTransactions({
          startDate: toTimestamp(practiceDateRange.from),
          endDate: toTimestamp(practiceDateRange.to),
          practiceRef,
        });

      const practiceAccountCredits = await this._getAccountCredits({
        startDate: toTimestamp(practiceDateRange.from),
        endDate: toTimestamp(practiceDateRange.to),
        practiceRef,
      });

      const uniqueAccountCreditRefs = uniqWith(
        [
          ...practiceAccountCreditTransactions.map(
            (transaction) => transaction.ref
          ),
          ...practiceAccountCredits.map((credit) => credit.ref),
        ],
        isSameRef
      );

      const accountCreditsFromOtherPractices = await asyncForAll(
        differenceWith(
          uniqueAccountCreditRefs,
          practiceAccountCredits,
          isSameRef
        ),
        (accountCreditRef) => Firestore.getDoc(accountCreditRef)
      );

      const allAccountCredits = [
        ...practiceAccountCredits,
        ...accountCreditsFromOtherPractices,
      ];

      const groupedAccountCreditTransactions = groupBy(
        practiceAccountCreditTransactions,
        (transaction) => transaction.ref.path
      );

      await of(allAccountCredits)
        .pipe(
          multiMergeMap(100, async (accountCredit) => {
            const patientRef = Firestore.getParentDocRef<IPatient>(
              accountCredit.ref
            );
            const patient = await Firestore.getDoc(patientRef);

            const accountCreditPractice = accountCredit.practiceRef
              ? practices.find((searchPractice) =>
                  isSameRef(searchPractice.ref, accountCredit.practiceRef)
                )
              : undefined;

            if (!accountCreditPractice) {
              return;
            }

            const usedTransactions =
              groupedAccountCreditTransactions[accountCredit.ref.path] ?? [];

            const usedTotal = roundTo2Decimals(
              sum(
                usedTransactions.map((transaction) =>
                  transaction.type === TransactionType.Outgoing
                    ? 0
                    : transaction.amount
                )
              )
            );

            const refundedTotal = sum(
              usedTransactions.map((transaction) => transaction.refunded)
            );

            const creditPracticeUsedAtDate = roundTo2Decimals(
              usedTotal - refundedTotal
            );

            if (
              !creditPracticeUsedAtDate &&
              !isSameRef(practiceRef, accountCredit.practiceRef)
            ) {
              return;
            }

            const creditPracticeAmount = isSameRef(
              practiceRef,
              accountCredit.practiceRef
            )
              ? roundTo2Decimals(accountCredit.amount)
              : 0;

            const creditPracticeRemainingAtDate = isSameRef(
              practiceRef,
              accountCredit.practiceRef
            )
              ? roundTo2Decimals(
                  creditPracticeAmount - creditPracticeUsedAtDate
                )
              : 0;

            const row = {
              creditCreatedAt: toISODate(
                accountCredit.createdAt,
                brand.settings.timezone
              ),
              creditPracticeName: accountCreditPractice.name,
              creditPatientName: patient.name,
              creditId: accountCredit.ref.id,
              creditPracticeAmount,
              creditPracticeUsedAtDate,
              creditPracticeRemainingAtDate,
            };

            rows.push(row);
          })
        )
        .toPromise();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }

    return this._toDataTables(sortBy(rows, (row) => row.creditCreatedAt));
  }

  private _toDataTables(
    data: IAccountCreditFromOtherPractice[]
  ): IDataTable<IAccountCreditFromOtherPractice> {
    return {
      name: 'Account Credits Used Up Until Date',
      data,
      columns: Object.entries(headers).map(([key, header]) => ({
        key,
        header,
      })),
    };
  }

  private async _getAccountCredits(
    request: IInvoiceReportRequest
  ): Promise<WithRef<IAccountCredit>[]> {
    return query(
      collectionGroupQuery<IAccountCredit>(
        CollectionGroup.AccountCredits,
        where('practiceRef', '==', request.practiceRef),
        where('deleted', '==', false),
        where('createdAt', '>=', toTimestamp(request.startDate)),
        where('createdAt', '<=', toTimestamp(request.endDate))
      )
    );
  }
}

async function getAccountCreditTransactions(
  request: ITransactionReportRequest
): Promise<IUsedAccountCreditWithType[]> {
  const totalRange = toTimePeriod(request.startDate, request.endDate);
  return from(chunkRange(totalRange, 2, 'weeks'))
    .pipe(
      mergeMap(
        (range) =>
          from(
            Firestore.getDocs(
              collectionGroupQuery<ITransaction>(
                CollectionGroup.Transactions,
                where('practiceRef', '==', request.practiceRef),
                where('deleted', '==', false),
                where('createdAt', '>=', toTimestamp(range.from)),
                where('createdAt', '<=', toTimestamp(range.to)),
                where('provider', 'in', [
                  TransactionProvider.AccountCredit,
                  TransactionProvider.AccountCreditTransfer,
                ])
              )
            )
          ).pipe(
            multiFilter(
              (transaction) => transaction.status === TransactionStatus.Complete
            ),
            multiMap((transaction) =>
              getAccountCreditUsed(transaction).map((record) => ({
                ...record,
                type: transaction.type,
                provider: transaction.provider,
              }))
            )
          ),
        4
      ),
      reduce2DArray(),
      scan(
        (acc, record) => [...acc, ...record],
        [] as IUsedAccountCreditWithType[]
      ),
      defaultIfEmpty([] as IUsedAccountCreditWithType[])
    )
    .toPromise();
}

function getAccountCreditUsed(
  transaction: WithRef<ITransaction>
): IUsedAccountCredit[] {
  const extendedData = transaction.extendedData;
  if (!isAccountCreditExtendedData(extendedData)) {
    return [];
  }
  return extendedData.accountCreditsUsed;
}
